/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function () {
  const derivedUiUpdateTimersByForm = new WeakMap();

  function schedulePlanDerivedUIUpdate(form, variantOverride, options) {
    if (!form) return;
    const immediate = options?.immediate !== false;
    const delaysMs = immediate ? [0, 60, 250] : [60, 250];

    const existing = derivedUiUpdateTimersByForm.get(form);
    if (existing) existing.forEach((id) => clearTimeout(id));

    const timers = delaysMs.map((delay) =>
      setTimeout(() => {
        updatePlanDerivedUI(form, variantOverride);
      }, delay)
    );
    derivedUiUpdateTimersByForm.set(form, timers);
  }

  function observeSealPurchaseOptionsContainer(container) {
    if (!(container instanceof Element)) return;
    if (container.dataset.pdcSealObserved === 'true') return;
    container.dataset.pdcSealObserved = 'true';

    const form = getProductFormFromElement(container);
    if (form) schedulePlanDerivedUIUpdate(form);

    const observer = new MutationObserver(() => {
      const form = getProductFormFromElement(container);
      if (!form) return;
      schedulePlanDerivedUIUpdate(form);
      normalizeSealSellingPlanInputs(form?.closest?.('.Product') || document);
    });

    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-checked', 'value'],
    });
  }

  function scanAndObserveSealContainers(root) {
    const scope = root || document;
    scope.querySelectorAll('.sls-purchase-options-container').forEach((container) => {
      observeSealPurchaseOptionsContainer(container);
    });
  }

  function initSealMerchantJsApiListeners() {
    if (document.documentElement?.dataset?.pdcSealMerchantApiInitialized === 'true') return;
    if (document.documentElement) document.documentElement.dataset.pdcSealMerchantApiInitialized = 'true';

    const handleSealEvent = (event) => {
      const element = event?.detail?.element;
      const form = element ? getProductFormFromElement(element) : null;

      if (form) {
        schedulePlanDerivedUIUpdate(form);
        normalizeSealSellingPlanInputs(form?.closest?.('.Product') || document);
      } else {
        document.querySelectorAll('form.ProductForm').forEach((form) => schedulePlanDerivedUIUpdate(form));
        normalizeSealSellingPlanInputs(document);
      }
    };

    // Fired when Seal renders a widget.
    document.addEventListener('sealsubs:subscription_widget_created', () => {
      scanAndObserveSealContainers(document);
      document.querySelectorAll('form.ProductForm').forEach((form) => schedulePlanDerivedUIUpdate(form));
    });

    // Fired when user changes purchase option / interval (includes discount/price info).
    document.addEventListener('sealsubs:price_update', handleSealEvent);
    // Fired when selected selling plan changes; for one-time it can be empty string.
    document.addEventListener('sealsubs:selling_plan_changed', handleSealEvent);
  }

  function parsePositiveInt(value, fallback) {
    const parsed = parseInt(String(value || ''), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
    return parsed;
  }

  function parseNumber(value, fallback) {
    const parsed = Number(String(value || '').trim());
    if (!Number.isFinite(parsed)) return fallback;
    return parsed;
  }

  function formatNumberCompact(value, maxDecimals) {
    if (!Number.isFinite(value)) return '';
    const decimals = Number.isFinite(maxDecimals) ? maxDecimals : 1;
    const rounded = Number(value.toFixed(decimals));
    return String(rounded);
  }

  function getQuantityInput(form) {
    return (
      form.querySelector('input[name="quantity"]:not([disabled])') ||
      form.querySelector('input[name="quantity"]')
    );
  }

  function setQuantity(form, quantity) {
    const quantityInput = getQuantityInput(form);
    if (!quantityInput) return;
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    quantityInput.value = String(quantity);
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function getActiveOption(container, selector) {
    if (!container) return null;
    return container.querySelector(`${selector}.is-active`) || container.querySelector(selector);
  }

  function normalizeSellingPlanId(value) {
    const match = String(value || '').match(/\d+/);
    return match ? match[0] : '';
  }

  function parseSellingPlanIdByDays(planContainer) {
    const script = planContainer?.querySelector('script[data-pdc-selling-plan-map]');
    if (!script) return new Map();

    let raw = '';
    try {
      raw = JSON.parse(script.textContent || '""') || '';
    } catch {
      raw = (script.textContent || '').trim();
    }

    const map = new Map();
    String(raw)
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [daysPart, idPart] = line.split(':');
        const days = parseInt(String(daysPart || '').trim(), 10);
        const sellingPlanId = normalizeSellingPlanId(idPart);
        if (Number.isFinite(days) && days > 0 && sellingPlanId) {
          map.set(days, sellingPlanId);
        }
      });

    return map;
  }

  function dispatchSealSelectedSellingPlanId(scope, sellingPlanId) {
    const root = scope || document;
    const widgetEl =
      root.querySelector('.sealsubs-container') ||
      root.querySelector('.sealsubs-target-element') ||
      document.querySelector('.sealsubs-container') ||
      document.querySelector('.sealsubs-target-element');
    if (!widgetEl) return false;

    const numericId = parseInt(String(sellingPlanId || ''), 10);
    const detailId = Number.isFinite(numericId) ? numericId : sellingPlanId;

    try {
      widgetEl.dispatchEvent(
        new CustomEvent('sealsubs:change_selected_selling_plan_id', {
          detail: { selling_plan_id: detailId },
        })
      );
      return true;
    } catch {
      return false;
    }
  }

  function normalizeSealSellingPlanInputs(scope) {
    const root = scope || document;
    const containers = Array.from(
      root.querySelectorAll('.sealsubs-container, .sealsubs-target-element, .Product')
    );
    if (containers.length === 0) containers.push(document);

    containers.forEach((container) => {
      const inputs = Array.from(container.querySelectorAll('input[name="selling_plan"]'));
      inputs.forEach((input) => {
        const normalized = normalizeSellingPlanId(input.value);
        if (!normalized) return;
        if (normalized === String(input.value || '')) return;
        input.value = normalized;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function parsePercentMap(planContainer, selector) {
    const script = planContainer?.querySelector(selector);
    if (!script) return new Map();

    let raw = '';
    try {
      raw = JSON.parse(script.textContent || '""') || '';
    } catch {
      raw = (script.textContent || '').trim();
    }

    const map = new Map();
    String(raw)
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [keyPart, valuePart] = line.split(':');
        const key = parseInt(String(keyPart || '').trim(), 10);
        const percent = parseNumber(String(valuePart || '').trim(), NaN);
        if (Number.isFinite(key) && key > 0 && Number.isFinite(percent) && percent >= 0) {
          map.set(key, percent);
        }
      });
    return map;
  }

  function getProductData(form) {
    const script = form.querySelector('script[data-product-json]') || document.querySelector('script[data-product-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  }

  function getSelectedVariantPriceCents(form, variantOverride) {
    if (variantOverride && typeof variantOverride.price !== 'undefined') {
      return typeof variantOverride.price === 'number'
        ? variantOverride.price
        : parseInt(String(variantOverride.price || ''), 10);
    }

    const productData = getProductData(form);
    const product = productData?.product;
    if (!product) return null;

    const selectedId =
      (productData?.selected_variant_id && String(productData.selected_variant_id)) ||
      (form.querySelector('input[name="id"]')?.value || '');
    const variant =
      (product.variants || []).find((v) => String(v.id) === String(selectedId)) || product.variants?.[0];
    if (!variant) return null;
    return typeof variant.price === 'number' ? variant.price : parseInt(String(variant.price || ''), 10);
  }

  function getSelectedVariantId(form, variantOverride) {
    if (variantOverride && typeof variantOverride.id !== 'undefined') {
      return String(variantOverride.id || '').trim();
    }

    const productData = getProductData(form);
    const selectedId =
      (productData?.selected_variant_id && String(productData.selected_variant_id)) ||
      (form.querySelector('input[name="id"]')?.value || '');
    return String(selectedId || '').trim();
  }

  function getCurrencyFormat() {
    if (window.theme?.currencyCodeEnabled) return window.theme.moneyWithCurrencyFormat || window.theme.moneyFormat;
    return window.theme?.moneyFormat;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');

      if (isNaN(number) || number == null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    let value = '';
    const match = formatString.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';

    switch (placeholder) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function formatMoneyCents(cents) {
    const format = getCurrencyFormat();
    return formatMoney(cents, format);
  }

  function getCurrentPurchaseType(form) {
    // Seal Subscriptions may render outside the product `<form>`, so scope to the product wrapper.
    // It also toggles UI via `.sls-option-container.sls-active`; `selling_plan` can lag behind.
    const scope = form?.closest?.('.Product') || document;

    // 1) Prefer the actual checked "subs_type_*" radio (Seal may not use `checked` on the nested inputs).
    const checkedSubsType = scope.querySelector('input.sls-option[name^="subs_type_"]:checked');
    if (checkedSubsType?.dataset?.type === 'subscription') return 'subscription';
    if (checkedSubsType?.dataset?.type === 'one_time') return 'one_time';

    // 2) Prefer aria state (what Seal uses for accessibility / UI state).
    const ariaCheckedOptions = Array.from(
      scope.querySelectorAll(
        '.sls-selling-plan-group-first-line[role="radio"][aria-checked="true"] input.sls-option'
      )
    );
    if (ariaCheckedOptions.some((el) => el?.dataset?.type === 'one_time')) return 'one_time';
    if (ariaCheckedOptions.some((el) => el?.dataset?.type === 'subscription')) return 'subscription';

    // 3) Fallback to `.sls-active` class (some themes/apps toggle this).
    const activeOptions = Array.from(scope.querySelectorAll('.sls-option-container.sls-active input.sls-option'));
    if (activeOptions.some((el) => el?.dataset?.type === 'one_time')) return 'one_time';
    if (activeOptions.some((el) => el?.dataset?.type === 'subscription')) return 'subscription';

    // 4) Last resort: hidden selling_plan value.
    const sellingPlanValue = (scope.querySelector('input[name="selling_plan"]')?.value || '').trim();
    if (sellingPlanValue) return 'subscription';

    return 'one_time';
  }

  function getProductFormFromElement(element) {
    const scope = element?.closest?.('.Product') || document;
    return scope.querySelector('form.ProductForm') || document.querySelector('form.ProductForm');
  }

  function formatMonthsLabel(monthsText) {
    const monthsNumber = parseNumber(monthsText, NaN);
    const normalized =
      Number.isFinite(monthsNumber) && Math.abs(monthsNumber - Math.round(monthsNumber)) < 1e-9
        ? String(Math.round(monthsNumber))
        : String(monthsText || '').trim();

    const plural = parseNumber(normalized, NaN) === 1 ? 'MONTH' : 'MONTHS';
    return `${normalized} ${plural}`;
  }

  function updateAddToCartButtonLabel(form, totalPriceCents, monthsText) {
    const button = form.querySelector('#ProductForm__AddToCartButton');
    if (!button) return;

    const priceHtml = `<span data-money-convertible>${formatMoneyCents(totalPriceCents)}</span>`;

    const purchaseType = getCurrentPurchaseType(form);
    if (purchaseType === 'one_time') {
      button.innerHTML = `<span class="pdc-atc-label">ADD TO CART - ${priceHtml}</span>`;
      return;
    }

    const monthsLabel = formatMonthsLabel(monthsText);
    button.innerHTML = `<span class="pdc-atc-label">ADD TO CART - ${priceHtml} / ${monthsLabel}</span>`;
  }

  function updateStickyCartBar(form, totalPriceCents, monthsText, bags) {
    const stickyRoot = document.querySelector('.StickyCartButton');
    if (!stickyRoot) return;

    const qtyBadge = stickyRoot.querySelector('[data-sticky-qty-badge]');
    if (qtyBadge) {
      const count = parsePositiveInt(bags, 1);
      qtyBadge.textContent = String(count);
      qtyBadge.classList.toggle('is-visible', count > 1);
    }

    const stickyQtyInput = stickyRoot.querySelector('input[name="quantity"][data-sticky-quantity]');
    if (stickyQtyInput) {
      stickyQtyInput.value = String(parsePositiveInt(bags, 1));
    }

    const stickyButton = stickyRoot.querySelector('[data-sticky-atc-button]');
    if (!stickyButton) return;

    const priceHtml = `<span data-money-convertible>${formatMoneyCents(totalPriceCents)}</span>`;
    const purchaseType = getCurrentPurchaseType(form);
    if (purchaseType === 'one_time') {
      stickyButton.innerHTML = `<span class="pdc-atc-label">ADD TO CART - ${priceHtml}</span>`;
      return;
    }

    const monthsLabel = formatMonthsLabel(monthsText);
    stickyButton.innerHTML = `<span class="pdc-atc-label">ADD TO CART - ${priceHtml} / ${monthsLabel}</span>`;
  }

  function syncStickyCartForm(mainForm, variantOverride) {
    const stickyForm = document.querySelector('.StickyCartButton form.ProductForm');
    if (!stickyForm) return;

    const selectedVariantId = getSelectedVariantId(mainForm, variantOverride);
    const stickyVariantIdInput = stickyForm.querySelector('input[name="id"]');
    if (stickyVariantIdInput && selectedVariantId) {
      stickyVariantIdInput.value = selectedVariantId;
    }

    const purchaseType = getCurrentPurchaseType(mainForm);
    const scope = mainForm?.closest?.('.Product') || document;
    const sellingPlanValue =
      (scope.querySelector('input[name="selling_plan"]')?.value || '').trim() ||
      (scope.querySelector('select[name="subs_interval"]')?.value || '').trim();
    const sellingPlanId = purchaseType === 'one_time' ? '' : normalizeSellingPlanId(sellingPlanValue);

    let stickySellingPlanInput = stickyForm.querySelector('input[name="selling_plan"]');
    if (!stickySellingPlanInput) {
      stickySellingPlanInput = document.createElement('input');
      stickySellingPlanInput.type = 'hidden';
      stickySellingPlanInput.name = 'selling_plan';
      stickyForm.appendChild(stickySellingPlanInput);
    }
    stickySellingPlanInput.value = sellingPlanId;
  }

  function updateGlobalPriceDisplays(form, variantOverride) {
    const planContainer = form.querySelector('[data-pdc-plan]');
    if (!planContainer) return;
    const activePlan = getActiveOption(planContainer, '.pdc-plan__option');
    if (!activePlan) return;

    const unitPriceCents = parseInt(activePlan.dataset.computedUnitPriceCents || '', 10);
    const totalPriceCents = parseInt(activePlan.dataset.computedTotalPriceCents || '', 10);
    const monthsText = activePlan.dataset.computedMonths || '';

    const hasUnit = Number.isFinite(unitPriceCents);
    const hasTotal = Number.isFinite(totalPriceCents);
    if (!hasUnit && !hasTotal) return;

    syncStickyCartForm(form, variantOverride);

    const productRoot = form.closest('.Product') || document;

    // Product meta price (per bag)
    if (hasUnit) {
      const metaPrice =
        productRoot.querySelector('.ProductMeta__PriceList .ProductMeta__Price.Price--highlight') ||
        productRoot.querySelector('.ProductMeta__PriceList .ProductMeta__Price') ||
        productRoot.querySelector('.ProductMeta__Price');
      if (metaPrice) metaPrice.innerHTML = formatMoneyCents(unitPriceCents);
    }

    // Add to cart button price (total for selected plan/bags)
    if (hasTotal) {
      updateAddToCartButtonLabel(form, totalPriceCents, monthsText);

      const bags = parsePositiveInt(activePlan.dataset.computedBags, 1);
      updateStickyCartBar(form, totalPriceCents, monthsText, bags);
    }

    // Seal widget displayed price for the active purchase option (per bag)
    if (hasUnit) {
      const activeSealMoney = form.querySelector('.sls-option-container.sls-active .money');
      if (activeSealMoney) activeSealMoney.innerHTML = formatMoneyCents(unitPriceCents);
    }
  }

  function getChewsPerDay(form) {
    const weightContainer = form.querySelector('[data-pdc-dog-weight]');
    const activeWeight = getActiveOption(weightContainer, '.pdc-dog-weight__option');
    return activeWeight ? parsePositiveInt(activeWeight.dataset.chewsPerDay, 1) : 1;
  }

  function getBagsPerShipment(planContainer, planOption) {
    if (!planOption) return 1;

    const explicitBags = parsePositiveInt(planOption.dataset.bagsPerShipment, 0);
    if (explicitBags > 0) return explicitBags;

    const qtyMode = (planContainer?.dataset.qtyMode || 'multiplier').toLowerCase();
    const multiplier = parsePositiveInt(planOption.dataset.qtyMultiplier, 1);
    const overrideQty = parsePositiveInt(planOption.dataset.qtyOverride, 1);
    return qtyMode === 'override' ? overrideQty : multiplier;
  }

  function computeSupply({ chewsPerBag, chewsPerDay, bagsPerShipment, daysPerMonth }) {
    const totalChews = bagsPerShipment * chewsPerBag;
    const days = chewsPerDay > 0 ? totalChews / chewsPerDay : totalChews;
    const months = daysPerMonth > 0 ? days / daysPerMonth : 0;
    return { totalChews, days, months };
  }

  function updatePlanDerivedUI(form, variantOverride) {
    const planContainer = form.querySelector('[data-pdc-plan]');
    if (!planContainer) return;

    const basePriceCents = getSelectedVariantPriceCents(form, variantOverride);
    const chewsPerBag = parsePositiveInt(planContainer.dataset.chewsPerBag, 90);
    const daysPerMonth = parsePositiveInt(planContainer.dataset.daysPerMonth, 30);
    const chewsPerDay = getChewsPerDay(form);
    const subscriptionDiscountPercent = parseNumber(planContainer.dataset.subscriptionDiscountPercent, 0);
    const discountStackMode = (planContainer.dataset.discountStackMode || 'multiplicative').toLowerCase();
    const priceDisplayMode = (planContainer.dataset.priceDisplayMode || 'per_bag').toLowerCase();
    const qtyDiscountByBags = parsePercentMap(planContainer, 'script[data-pdc-qty-discount-map]');
    const purchaseType = getCurrentPurchaseType(form);

    const planOptions = Array.from(planContainer.querySelectorAll('.pdc-plan__option'));
    planOptions.forEach((option) => {
      const bagsPerShipment = getBagsPerShipment(planContainer, option);
      const supply = computeSupply({ chewsPerBag, chewsPerDay, bagsPerShipment, daysPerMonth });
      const monthsText = formatNumberCompact(supply.months, 1);

      const nameEl = option.querySelector('.pdc-plan__name');
      if (nameEl && monthsText) nameEl.textContent = `${monthsText} Months supply`;

      const qtyDiscountPercent = qtyDiscountByBags.get(bagsPerShipment) || 0;
      const hasSubDiscount = purchaseType === 'subscription' && subscriptionDiscountPercent > 0;
      const combinedDiscountPercent =
        hasSubDiscount && qtyDiscountPercent > 0
          ? discountStackMode === 'additive'
            ? subscriptionDiscountPercent + qtyDiscountPercent
            : (1 -
                (1 - subscriptionDiscountPercent / 100) * (1 - qtyDiscountPercent / 100)) *
              100
          : hasSubDiscount
            ? subscriptionDiscountPercent
            : qtyDiscountPercent;

      const savingsEl = option.querySelector('.pdc-plan__savings');
      if (savingsEl) {
        const rounded = Math.round(combinedDiscountPercent);
        savingsEl.textContent = rounded > 0 ? `SAVE ${rounded}%` : '';
        savingsEl.classList.toggle('is-hidden', rounded <= 0);
      }

      if (basePriceCents != null && Number.isFinite(basePriceCents)) {
        const discountedUnitPriceCents = Math.round(basePriceCents * (1 - combinedDiscountPercent / 100));
        const totalPriceCents = discountedUnitPriceCents * bagsPerShipment;
        const shownPriceCents = priceDisplayMode === 'per_shipment' ? totalPriceCents : discountedUnitPriceCents;

        const priceEl = option.querySelector('.pdc-plan__price');
        if (priceEl) priceEl.innerHTML = `<span data-money-convertible>${formatMoneyCents(shownPriceCents)}</span>`;

        const pricePerDayCents = supply.days > 0 ? Math.round(totalPriceCents / supply.days) : totalPriceCents;
        const subEl = option.querySelector('.pdc-plan__sub');
        if (subEl) subEl.innerHTML = `<span data-money-convertible>${formatMoneyCents(pricePerDayCents)}</span> / day`;

        option.dataset.computedUnitPriceCents = String(discountedUnitPriceCents);
        option.dataset.computedTotalPriceCents = String(totalPriceCents);
        option.dataset.computedDiscountPercent = String(combinedDiscountPercent);
      }

      option.dataset.computedDays = String(Math.round(supply.days));
      option.dataset.computedMonths = monthsText;
      option.dataset.computedTotalChews = String(Math.round(supply.totalChews));
      option.dataset.computedChewsPerDay = String(chewsPerDay);
      option.dataset.computedBags = String(bagsPerShipment);
    });

    const activePlan = getActiveOption(planContainer, '.pdc-plan__option');
    const summary = planContainer.querySelector('[data-pdc-plan-summary]');
    if (summary && activePlan) {
      if (purchaseType === 'one_time') {
        summary.textContent = '';
        summary.classList.add('is-hidden');
      } else {
        const bags = parsePositiveInt(activePlan.dataset.computedBags, 1);
        const months = activePlan.dataset.computedMonths || '';
        const days = parsePositiveInt(activePlan.dataset.computedDays, 0);
        summary.textContent = `Adds ${bags} bag${bags === 1 ? '' : 's'} to cart • Ships every ${months} month${months === '1' ? '' : 's'} (${days} days)`;
        summary.classList.remove('is-hidden');
      }
    }

    updateGlobalPriceDisplays(form, variantOverride);
  }

  function applyQuantityFromSelectors(form) {
    const planContainer = form.querySelector('[data-pdc-plan]');
    const activePlanOption = getActiveOption(planContainer, '.pdc-plan__option');
    if (!activePlanOption) return;
    const bagsPerShipment = getBagsPerShipment(planContainer, activePlanOption);
    setQuantity(form, bagsPerShipment);
  }

  function applySealPlanSelection(form, planOption) {
    if (!planOption) return;

    const purchaseType = getCurrentPurchaseType(form);
    const intendedPurchaseType = (planOption.dataset.purchaseType || 'subscription').toLowerCase();
    let sellingPlanId = normalizeSellingPlanId(planOption.dataset.sellingPlanId);

    const scope = form || document;
    const subscriptionRadio = scope.querySelector('input.sls-option[data-type="subscription"]');
    const oneTimeRadio = scope.querySelector('input.sls-option[data-type="one_time"]');

    // If the customer explicitly selected one-time purchase, keep it that way when changing plans.
    // Plan buttons should not bounce the Seal purchase option back to subscription.
    if (purchaseType === 'one_time' && intendedPurchaseType === 'subscription') {
      const intervalSelect = scope.querySelector('select[name="subs_interval"]');
      if (intervalSelect && intervalSelect.value) {
        intervalSelect.value = '';
        intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const hiddenSellingPlan = scope.querySelector('input[name="selling_plan"]');
      if (hiddenSellingPlan && hiddenSellingPlan.value) {
        hiddenSellingPlan.value = '';
        hiddenSellingPlan.dispatchEvent(new Event('change', { bubbles: true }));
      }

      dispatchSealSelectedSellingPlanId(scope, '');
      normalizeSealSellingPlanInputs(scope);
      return;
    }

    if (intendedPurchaseType === 'one_time') {
      if (subscriptionRadio && purchaseType === 'subscription' && oneTimeRadio) oneTimeRadio.click();

      const intervalSelect = scope.querySelector('select[name="subs_interval"]');
      if (intervalSelect && intervalSelect.value) {
        intervalSelect.value = '';
        intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const hiddenSellingPlan = scope.querySelector('input[name="selling_plan"]');
      if (hiddenSellingPlan && hiddenSellingPlan.value) {
        hiddenSellingPlan.value = '';
        hiddenSellingPlan.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Seal JS API: pass empty string to select one-time purchase.
      dispatchSealSelectedSellingPlanId(scope, '');
      normalizeSealSellingPlanInputs(scope);
      return;
    }

    if (intendedPurchaseType === 'subscription' && subscriptionRadio && purchaseType === 'one_time') {
      subscriptionRadio.click();
    }

    if (!sellingPlanId) {
      const planContainer = form?.querySelector('[data-pdc-plan]');
      const map = parseSellingPlanIdByDays(planContainer);
      const computedDays = parsePositiveInt(planOption.dataset.computedDays, 0);
      if (computedDays > 0) {
        if (map.has(computedDays)) {
          sellingPlanId = map.get(computedDays) || '';
        } else if (map.size > 0) {
          let closestDays = null;
          let closestDelta = Infinity;
          for (const days of map.keys()) {
            const delta = Math.abs(days - computedDays);
            if (delta < closestDelta) {
              closestDelta = delta;
              closestDays = days;
            }
          }
          if (closestDays != null && closestDelta <= 2) {
            sellingPlanId = map.get(closestDays) || '';
          }
        }
      }
    }
    sellingPlanId = normalizeSellingPlanId(sellingPlanId);
    if (!sellingPlanId) return;

    const intervalSelect = scope.querySelector('select[name="subs_interval"]');
    if (intervalSelect) {
      const hasOption = Array.from(intervalSelect.options || []).some(
        (opt) => String(opt.value) === sellingPlanId
      );
      if (hasOption) {
        intervalSelect.value = sellingPlanId;
        intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const hiddenSellingPlan = scope.querySelector('input[name="selling_plan"]');
    if (hiddenSellingPlan) {
      hiddenSellingPlan.value = sellingPlanId;
      hiddenSellingPlan.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Prefer the Seal Subscriptions JS API to avoid the widget snapping back to its default interval.
    dispatchSealSelectedSellingPlanId(scope, sellingPlanId);
    // Some Seal versions write non-numeric values into the hidden input (e.g. "days 123"); normalize it.
    normalizeSealSellingPlanInputs(scope);
  }

  function initDogWeightSelector(root) {
    root.querySelectorAll('[data-pdc-dog-weight]').forEach((container) => {
      if (container.dataset.pdcDogWeightInitialized === 'true') return;
      container.dataset.pdcDogWeightInitialized = 'true';

      const buttons = Array.from(container.querySelectorAll('.pdc-dog-weight__option'));
      if (buttons.length === 0) return;

      const defaultKey = (container.dataset.default || '').trim();
      const defaultButton =
        buttons.find((button) => (button.dataset.weight || '') === defaultKey) || buttons[0];

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const form = container.closest('form');
          if (!form) return;

          buttons.forEach((b) => {
            const isActive = b === button;
            b.classList.toggle('is-active', isActive);
            b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });

	          updatePlanDerivedUI(form);
	          applyQuantityFromSelectors(form);
	          const planContainer = form.querySelector('[data-pdc-plan]');
	          const activePlanOption = getActiveOption(planContainer, '.pdc-plan__option');
	          if (activePlanOption) {
	            applySealPlanSelection(form, activePlanOption);
	            // Seal updates purchase option / selling plan async; re-run after it settles.
	            schedulePlanDerivedUIUpdate(form);
	          }

	          container.dispatchEvent(
	            new CustomEvent('pdc:dog-weight:changed', {
	              bubbles: true,
	              detail: {
                weight: button.dataset.weight || '',
                chewsPerDay: parsePositiveInt(button.dataset.chewsPerDay, 1),
                label: (button.textContent || '').trim(),
              },
            })
          );
        });
      });

      const form = container.closest('form');
      if (!form) return;
      defaultButton.click();
    });
  }

  function initPlanSelector(root) {
    root.querySelectorAll('[data-pdc-plan]').forEach((container) => {
      if (container.dataset.pdcPlanInitialized === 'true') return;
      container.dataset.pdcPlanInitialized = 'true';

      const form = container.closest('form');
      if (!form) return;

      const buttons = Array.from(container.querySelectorAll('.pdc-plan__option'));
      if (buttons.length === 0) return;

      const defaultKey = (container.dataset.default || '').trim();
      const defaultButton =
        buttons.find((button) => (button.dataset.planKey || '') === defaultKey) || buttons[0];

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          buttons.forEach((b) => {
            const isActive = b === button;
            b.classList.toggle('is-active', isActive);
            b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });

          updatePlanDerivedUI(form);
          applyQuantityFromSelectors(form);
          applySealPlanSelection(form, button);
          // Seal updates purchase option / selling plan async; re-run after it settles to avoid double-click mismatches.
          schedulePlanDerivedUIUpdate(form);

          const qtyMode = (container.dataset.qtyMode || 'multiplier').toLowerCase();
          const chewsPerBag = parsePositiveInt(container.dataset.chewsPerBag, 90);
          const daysPerMonth = parsePositiveInt(container.dataset.daysPerMonth, 30);
          const chewsPerDay = getChewsPerDay(form);
          const bagsPerShipment = getBagsPerShipment(container, button);
          const supply = computeSupply({ chewsPerBag, chewsPerDay, bagsPerShipment, daysPerMonth });
          const computedDays = Math.round(supply.days);
          const computedMonths = formatNumberCompact(supply.months, 1);

          const detail = {
            planKey: button.dataset.planKey || '',
            purchaseType: (button.dataset.purchaseType || 'subscription').toLowerCase(),
            sellingPlanId: (button.dataset.sellingPlanId || '').trim(),
            quantityMode: qtyMode,
            quantityMultiplier: parsePositiveInt(button.dataset.qtyMultiplier, 1),
            quantityOverride: parsePositiveInt(button.dataset.qtyOverride, 1),
            bagsPerShipment,
            chewsPerDay,
            chewsPerBag,
            totalChewsShipped: Math.round(supply.totalChews),
            shipEveryDays: computedDays,
            shipEveryMonths: parseNumber(computedMonths, supply.months),
            label: (button.textContent || '').trim(),
          };

          container.dispatchEvent(
            new CustomEvent('pdc:plan:changed', {
              bubbles: true,
              detail,
            })
          );
        });
      });

      updatePlanDerivedUI(form);
      defaultButton.click();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initDogWeightSelector(document);
    initPlanSelector(document);
    initSealMerchantJsApiListeners();

    // Seal injects its UI async; observe DOM for the widget and keep derived prices in sync.
    scanAndObserveSealContainers(document);
    const sealInjectionObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes || []) {
          if (!(node instanceof Element)) continue;
          if (node.matches('.sls-purchase-options-container')) observeSealPurchaseOptionsContainer(node);
          node
            .querySelectorAll?.('.sls-purchase-options-container')
            .forEach((container) => observeSealPurchaseOptionsContainer(container));
        }
      }
    });
    if (document.body) {
      sealInjectionObserver.observe(document.body, { subtree: true, childList: true });
    }
  });
  document.addEventListener('shopify:section:load', (event) => {
    initDogWeightSelector(event.target);
    initPlanSelector(event.target);
  });

  document.addEventListener('variant:changed', (event) => {
    const variant = event.detail?.variant;
    document.querySelectorAll('form.ProductForm').forEach((form) => {
      updatePlanDerivedUI(form, variant);
    });
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement) {
      if (input.classList.contains('sls-option')) {
        const form = getProductFormFromElement(input);
        if (!form) return;
        schedulePlanDerivedUIUpdate(form);
        normalizeSealSellingPlanInputs(form?.closest?.('.Product') || document);
        return;
      }
      if (input.name === 'selling_plan') {
        const form = getProductFormFromElement(input);
        if (form) schedulePlanDerivedUIUpdate(form);
        normalizeSealSellingPlanInputs(input.closest('.Product') || document);
      }
    } else if (input instanceof HTMLSelectElement) {
      if (input.name === 'subs_interval') {
        const form = getProductFormFromElement(input);
        if (form) schedulePlanDerivedUIUpdate(form);
        normalizeSealSellingPlanInputs(input.closest('.Product') || document);
      }
    }
  });

  // Some subscription widgets toggle via container clicks; keep derived prices in sync.
  document.addEventListener('click', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (!el.closest('.sls-option-container, .sls-purchase-options-container, .sealsubs-container, .sealsubs-target-element'))
      return;
    const form = getProductFormFromElement(el);
    if (!form) return;
    // Allow Seal to toggle state async before recomputing derived discounts/prices.
    schedulePlanDerivedUIUpdate(form);
  });
})();
